import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import './AdminMarketing.css';

const serverConfig = { withCredentials: true };

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const SEGMENTS = [
  { key: 'all',          label: 'All Users',             desc: 'Everyone who signed up and hasn\'t unsubscribed' },
  { key: 'ai_access',   label: 'AI Access Users',        desc: 'Users with aiAccess enabled' },
  { key: 'new_30d',     label: 'New (last 30 days)',      desc: 'Users who signed up in the last 30 days' },
  { key: 'active_nodes',label: 'Users with Active Records', desc: 'Users who have at least one active record' },
];

const SEGMENT_LABELS = Object.fromEntries(SEGMENTS.map((s) => [s.key, s.label]));

const TEMPLATES = [
  {
    key: 'clean',
    label: 'Clean',
    desc: 'Orange wordmark, white background. Works for anything.',
    preview: { top: '#f17501', bar: false },
  },
  {
    key: 'announcement',
    label: 'Announcement',
    desc: 'Orange header with subject as headline. Great for launches.',
    preview: { top: '#f17501', bar: true },
  },
  {
    key: 'update',
    label: 'Product Update',
    desc: 'Orange accent bar. Best for changelogs and new features.',
    preview: { top: '#fff', bar: true, accent: true },
  },
  {
    key: 'personal',
    label: 'Personal',
    desc: 'No branding, no header. Reads like a direct email.',
    preview: { top: '#fff', bar: false },
  },
];

const TEMPLATE_LABELS = Object.fromEntries(TEMPLATES.map((t) => [t.key, t.label]));

const CONTENT_STARTERS = [
  {
    label: 'New Feature',
    subject: 'Something new on dmyor',
    body: `Hey {{name}},

# We just shipped something new.

## What's new

[Describe the feature here]

## Why it matters

[Explain the value for the user]

Try it out → https://dmyor.com/dashboard

The dmyor team`,
  },
  {
    label: 'Re-engagement',
    subject: 'Hey {{name}}, your vision is still waiting',
    body: `Hey {{name}},

You started something on dmyor, and it's still there, waiting for you.

The reality you described doesn't build itself. Every day you wait is a day you're not moving toward it.

Come back and pick up where you left off.

→ https://dmyor.com/dashboard

The dmyor team`,
  },
  {
    label: 'Weekly Update',
    subject: 'What\'s been happening at dmyor',
    body: `Hey {{name}},

## This week on dmyor

[Summary of what happened]

## Coming up

[What's next]

As always, we're building this for you. Reply to this email if you have thoughts.

The dmyor team`,
  },
  {
    label: 'Blank',
    subject: '',
    body: '',
  },
];

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const AdminMarketing = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const [audienceMode, setAudienceMode] = useState('segment'); // 'segment' | 'targeted'
  const [segment, setSegment] = useState('all');
  const [previewCount, setPreviewCount] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const [recipientEmails, setRecipientEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');

  const [template, setTemplate] = useState('clean');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchCampaigns(); }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${config.apiUrl}/api/v1/admin/marketing/campaigns`, serverConfig);
      setCampaigns(data.campaigns);
    } catch {
      toast.error('Failed to load campaign history');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = useCallback(async (seg) => {
    try {
      setPreviewing(true);
      setPreviewCount(null);
      const { data } = await axios.get(
        `${config.apiUrl}/api/v1/admin/marketing/preview?segment=${seg}`,
        serverConfig
      );
      setPreviewCount(data.count);
    } catch {
      toast.error('Failed to fetch segment count');
    } finally {
      setPreviewing(false);
    }
  }, []);

  const handleSegmentChange = (seg) => {
    setSegment(seg);
    fetchPreview(seg);
  };

  useEffect(() => { fetchPreview('all'); }, [fetchPreview]);

  const addEmail = (raw) => {
    const emails = raw.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean);
    const valid = emails.filter(isValidEmail);
    const dupes = valid.filter((e) => recipientEmails.includes(e));
    const toAdd = valid.filter((e) => !recipientEmails.includes(e));
    if (toAdd.length) setRecipientEmails((prev) => [...prev, ...toAdd]);
    if (dupes.length) toast.info(`Already added: ${dupes.join(', ')}`);
    if (emails.length > valid.length) toast.error('Some entries are not valid emails and were skipped');
  };

  const handleEmailInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      if (emailInput.trim()) { addEmail(emailInput); setEmailInput(''); }
    } else if (e.key === 'Backspace' && !emailInput && recipientEmails.length) {
      setRecipientEmails((prev) => prev.slice(0, -1));
    }
  };

  const removeEmail = (email) => setRecipientEmails((prev) => prev.filter((e) => e !== email));

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return toast.error('Subject and body are required');

    if (audienceMode === 'targeted') {
      if (recipientEmails.length === 0) return toast.error('Add at least one recipient email');
      if (!window.confirm(`Send to ${recipientEmails.length} specific recipient${recipientEmails.length !== 1 ? 's' : ''}?`)) return;
      try {
        setSending(true);
        const { data } = await axios.post(
          `${config.apiUrl}/api/v1/admin/marketing/send`,
          { subject, body, template, recipients: recipientEmails },
          serverConfig
        );
        toast.success(`Sent to ${data.sent}${data.failed ? `, ${data.failed} failed` : ''}`);
        fetchCampaigns();
      } catch {
        toast.error('Failed to send');
      } finally {
        setSending(false);
      }
      return;
    }

    if (!window.confirm(`Send to ~${previewCount ?? '?'} users in "${SEGMENT_LABELS[segment]}"?`)) return;
    try {
      setSending(true);
      const { data } = await axios.post(
        `${config.apiUrl}/api/v1/admin/marketing/send`,
        { subject, body, segment, template },
        serverConfig
      );
      toast.success(`Sent to ${data.sent} user${data.sent !== 1 ? 's' : ''}${data.failed ? `, ${data.failed} failed` : ''}`);
      fetchCampaigns();
    } catch {
      toast.error('Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="am-page">
      <div className="am-header">
        <div className="am-eyebrow">Admin · Marketing</div>
        <h1 className="am-title">Send Updates to Users</h1>
        <p className="am-sub">Compose and send personalised emails to segments of your user base.</p>
      </div>

      <div className="am-body">
        {/* Left: Compose */}
        <div className="am-compose-wrap">
          {/* Audience */}
          <div className="am-section">
            <div className="am-section-title">Audience</div>
            <div className="am-audience-tabs">
              <button
                className={`am-audience-tab ${audienceMode === 'segment' ? 'active' : ''}`}
                onClick={() => setAudienceMode('segment')}
              >Segment</button>
              <button
                className={`am-audience-tab ${audienceMode === 'targeted' ? 'active' : ''}`}
                onClick={() => setAudienceMode('targeted')}
              >Specific recipients</button>
            </div>

            {audienceMode === 'segment' ? (
              <>
                <div className="am-segments">
                  {SEGMENTS.map((s) => (
                    <div
                      key={s.key}
                      className={`am-segment-card ${segment === s.key ? 'active' : ''}`}
                      onClick={() => handleSegmentChange(s.key)}
                    >
                      <div className="am-segment-label">{s.label}</div>
                      <div className="am-segment-desc">{s.desc}</div>
                    </div>
                  ))}
                </div>
                <div className="am-preview-count">
                  {previewing ? (
                    <span className="am-preview-loading">Counting…</span>
                  ) : previewCount !== null ? (
                    <span><strong>{previewCount}</strong> recipient{previewCount !== 1 ? 's' : ''} in this segment</span>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="am-targeted-wrap">
                <div className="am-tag-input-box" onClick={() => document.getElementById('am-email-input').focus()}>
                  {recipientEmails.map((email) => (
                    <span key={email} className="am-email-tag">
                      {email}
                      <button className="am-email-tag-remove" onClick={(e) => { e.stopPropagation(); removeEmail(email); }}>×</button>
                    </span>
                  ))}
                  <input
                    id="am-email-input"
                    className="am-tag-input"
                    placeholder={recipientEmails.length === 0 ? 'Type email and press Enter…' : ''}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={handleEmailInputKeyDown}
                    onBlur={() => { if (emailInput.trim()) { addEmail(emailInput); setEmailInput(''); } }}
                  />
                </div>
                <p className="am-targeted-hint">
                  Press <kbd>Enter</kbd> or <kbd>,</kbd> after each address. Paste a comma-separated list to add multiple at once.
                </p>
                {recipientEmails.length > 0 && (
                  <div className="am-preview-count">
                    <strong>{recipientEmails.length}</strong> recipient{recipientEmails.length !== 1 ? 's' : ''}
                    <button className="am-clear-all" onClick={() => setRecipientEmails([])}>Clear all</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Content starters */}
          <div className="am-section">
            <div className="am-section-title">Start from a template</div>
            <div className="am-starters">
              {CONTENT_STARTERS.map((s) => (
                <button
                  key={s.label}
                  className="am-starter-btn"
                  onClick={() => { setSubject(s.subject); setBody(s.body); }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Layout */}
          <div className="am-section">
            <div className="am-section-title">Email Layout</div>
            <div className="am-templates">
              {TEMPLATES.map((t) => (
                <div
                  key={t.key}
                  className={`am-template-card ${template === t.key ? 'active' : ''}`}
                  onClick={() => setTemplate(t.key)}
                >
                  <div className="am-template-thumb">
                    {t.preview.bar ? (
                      <div className="am-thumb-bar" style={{ background: t.preview.top }} />
                    ) : (
                      <div className="am-thumb-logo" style={{ color: t.preview.top === '#fff' ? '#ddd' : '#f17501' }}>▪</div>
                    )}
                    <div className="am-thumb-lines">
                      <div className="am-thumb-line am-thumb-line-h" />
                      <div className="am-thumb-line" />
                      <div className="am-thumb-line am-thumb-line-short" />
                    </div>
                  </div>
                  <div className="am-template-label">{t.label}</div>
                  <div className="am-template-desc">{t.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Compose */}
          <div className="am-section">
            <div className="am-section-title">Compose</div>
            <div className="am-compose-hint">
              <code>{'# Heading'}</code> &nbsp;·&nbsp; <code>{'## Subheading'}</code> &nbsp;·&nbsp; <code>{'**bold**'}</code> &nbsp;·&nbsp; <code>{'*italic*'}</code> &nbsp;·&nbsp; <code>{'{{name}}'}</code> to personalise.
              Unsubscribe link appended automatically.
            </div>

            <div className="am-field">
              <label className="am-label">Subject</label>
              <input
                className="am-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject line…"
              />
            </div>

            <div className="am-field">
              <label className="am-label">Body</label>
              <textarea
                className="am-textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
              />
            </div>

            <button
              className="am-send-btn"
              onClick={handleSend}
              disabled={sending || (audienceMode === 'segment' && previewCount === 0) || (audienceMode === 'targeted' && recipientEmails.length === 0)}
            >
              {sending
                ? 'Sending…'
                : audienceMode === 'targeted'
                  ? `Send to ${recipientEmails.length} recipient${recipientEmails.length !== 1 ? 's' : ''}`
                  : `Send to ${previewCount !== null ? previewCount : '…'} user${previewCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>

        {/* Right: Campaign history */}
        <div className="am-history">
          <div className="am-section-title">Campaign History</div>

          {loading ? (
            <p className="am-loading">Loading…</p>
          ) : campaigns.length === 0 ? (
            <p className="am-empty">No campaigns sent yet.</p>
          ) : (
            <div className="am-campaign-list">
              {campaigns.map((c) => (
                <div key={c._id} className="am-campaign-row">
                  <div className="am-campaign-top">
                    <span className="am-campaign-subject">{c.subject}</span>
                    <span className="am-campaign-date">{formatDate(c.sentAt)}</span>
                  </div>
                  <div className="am-campaign-meta">
                    <span className={`am-segment-pill${c.segment === 'targeted' ? ' am-segment-pill--targeted' : ''}`}>
                      {c.segment === 'targeted'
                        ? `Targeted · ${c.recipientEmails?.length ?? c.recipientCount}`
                        : (SEGMENT_LABELS[c.segment] || c.segment)}
                    </span>
                    {c.template && c.template !== 'clean' && (
                      <span className="am-template-pill">{TEMPLATE_LABELS[c.template] || c.template}</span>
                    )}
                    <span className="am-campaign-count">
                      {c.recipientCount} sent
                      {c.failedCount > 0 && ` · ${c.failedCount} failed`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMarketing;
